import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate, getMonthsShort, getStatusySpacer, statusSpacerMeta } from '../utils';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import Modal from '../components/Modal';
import { Btn } from '../components/Input';
import { IconPlus, IconDoc, IconEdit, IconChevronRight, IconLink, IconCheck, IconAlert, IconEye } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import Spacery from './Spacery';
import { useToast } from '../components/Toast';
import { Skeleton, SkeletonCard } from '../components/Skeleton';
import FakturyList from '../components/FakturyList';
import PakietForm from '../components/PakietForm';
import KlientForm from '../components/KlientForm';
import HelpHint from '../components/HelpHint';
import {
  ZaplacPakietModal,
  WystawFaktureModal,
  OznaczFaktureZaplaconaModal,
} from './KlientDetail';

const TODAY = new Date().toISOString().slice(0, 10);

// Statusy dostępne jako filtr raportu (kolejność jak w utils.js)
const RAPORT_STATUSY = ['aktywny', 'zamrozony', 'przekazany', 'usuniety'];

// Bazowy host portalu klienta — w prod my.scanpilots.com, lokalnie ten sam host na ścieżce /k.
// Daje "kopiowalny" link bez zwracania go z backendu.
function klientPortalUrl(slug) {
  if (!slug) return null;
  const host = window.location.hostname;
  if (host === 'app.scanpilots.com') return `https://my.scanpilots.com/${slug}`;
  if (host === 'localhost' || host === '127.0.0.1') return `${window.location.origin}/k/${slug}`;
  // produkcja na innym domenie / staging → użyj tej samej domeny pod /k/<slug>
  return `${window.location.origin}/k/${slug}`;
}

// ─── Moduł "Panel klienta" — kopiowanie linku + skrót do raportu ─────────────
function KlientPanelModule({ slug, klient }) {
  const toast = useToast();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const url = klientPortalUrl(slug);
  const hasEmail = !!(klient?.email && klient.email.trim());

  const openPreview = async () => {
    if (!klient?.id || !url) return;
    setPreviewing(true);
    try {
      const { token } = await api.klientPreviewToken(klient.id);
      // Token przekazujemy w hashu (fragment nigdy nie trafia do nagłówka Referer).
      // KlientPortal czyta hash przy starcie, przepisuje do localStorage i czyści URL.
      // Bez 'noopener' — dzięki temu karta podglądu może sama się zamknąć (window.close)
      // po kliknięciu "Zamknij podgląd", wracając fotografa do panelu klienta.
      window.open(`${url}#preview_token=${encodeURIComponent(token)}`, '_blank');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('clientPanel.linkCopied'));
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error(t('clientPanel.copyFailed'));
    }
  };

  if (!slug) {
    return (
      <div className="ds-card" style={{ padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <IconAlert className="w-4 h-4" style={{ color: 'var(--err2)', flexShrink: 0 }} />
        <p style={{ fontSize: '11px', color: 'var(--ink2)', flex: 1 }}>
          {t('clientPanel.noSlugPre')}<strong>{t('clientPanel.noSlugBold')}</strong>{t('clientPanel.noSlugPost')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="ds-card"
      style={{
        padding: '18px 20px',
        marginBottom: '16px',
        background: 'linear-gradient(135deg, rgba(184,84,40,0.08) 0%, rgba(184,84,40,0.02) 100%)',
        borderColor: 'rgba(184,84,40,0.25)',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0, flex: '1 1 280px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <IconLink className="w-3.5 h-3.5" style={{ color: 'var(--acc2)' }} />
            <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--acc2)', textTransform: 'uppercase' }}>
              {t('clientPanel.title')}
            </p>
          </div>
          <p
            onClick={copyLink}
            title={t('clientPanel.clickToCopy')}
            style={{
              fontSize: '12px',
              fontWeight: 700,
              color: 'var(--ink)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              cursor: 'pointer',
              wordBreak: 'break-all',
              userSelect: 'all',
              marginBottom: '4px',
            }}
          >
            {url}
          </p>
          <p style={{ fontSize: '10px', color: 'var(--ink3)', lineHeight: 1.5 }}>
            {hasEmail
              ? <>{t('clientPanel.loginHintPre')}<strong style={{ color: 'var(--ink2)' }}>{klient.email}</strong>{t('clientPanel.loginHintPost')}</>
              : <span style={{ color: 'var(--err2)' }}>{t('clientPanel.noEmail')}</span>}
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <Btn variant={copied ? 'success' : 'primary'} onClick={copyLink}>
            <span className="flex items-center gap-1.5">
              {copied
                ? <><IconCheck className="w-3 h-3" /> {t('clientPanel.copied')}</>
                : <><IconLink className="w-3 h-3" /> {t('clientPanel.copyLink')}</>}
            </span>
          </Btn>
          <Btn variant="ghost" onClick={openPreview} disabled={previewing} title={t('clientPanel.previewTitle')}>
            <span className="flex items-center gap-1.5">
              <IconEye className="w-3 h-3" /> {previewing ? t('clientPanel.opening') : t('clientPanel.preview')}
            </span>
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Moduł "Raport klienta" — inline parametry generowanego raportu ─────────
function RaportModule({ klient, disabled }) {
  const { t } = useTranslation();
  const toast = useToast();
  const spacery = klient.spacery || [];
  const pakiety = klient.pakiety || [];

  const [scope, setScope] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pakietId, setPakietId] = useState(pakiety[0]?.id || '');
  const [symulacja, setSymulacja] = useState(false);
  const [symData, setSymData] = useState('');
  const [loading, setLoading] = useState(false);
  // Filtr statusów w raporcie — domyślnie wszystkie zaznaczone.
  const [statusFilter, setStatusFilter] = useState(new Set(RAPORT_STATUSY));

  const toggleId = id => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleStatus = value => setStatusFilter(prev => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });

  const includedSpaces = useMemo(() => {
    let arr = spacery;
    if (scope === 'selected') arr = spacery.filter(s => selectedIds.has(s.id));
    else if (scope === 'pakiet') arr = spacery.filter(s => Number(s.pakiet_id) === Number(pakietId));
    return arr.filter(s => statusFilter.has(s.status));
  }, [scope, selectedIds, pakietId, spacery, statusFilter]);

  const symTotal = useMemo(() => {
    if (!symulacja || !symData) return null;
    return includedSpaces.filter(s => s.status === 'aktywny').reduce((sum, s) => {
      const od = s.podstawa_naliczania || s.koniec_darmowego;
      if (!od || symData <= od) return sum;
      const [y1, m1] = od.split('-').map(Number);
      const [y2, m2] = symData.split('-').map(Number);
      return sum + Math.max(0, (y2 - y1) * 12 + (m2 - m1)) * s.stawka_hostingu;
    }, 0);
  }, [symulacja, symData, includedSpaces]);

  const canGenerate = !disabled && !loading && includedSpaces.length > 0 && (!symulacja || !!symData);

  const getKonfig = () => ({
    scope,
    spacery_ids: scope === 'selected' ? [...selectedIds] : [],
    pakiet_id: scope === 'pakiet' ? pakietId : null,
    symulacja_data: symulacja && symData ? symData : null,
    // Wszystkie zaznaczone = brak filtra (pusta lista); inaczej wyślij wybrane statusy.
    statusy: statusFilter.size === RAPORT_STATUSY.length ? [] : [...statusFilter],
  });

  const handlePobierz = async () => {
    if (!canGenerate) return;
    setLoading(true);
    try {
      const blob = await api.pobierzRaportPDF(klient.id, getKonfig());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `raport-${klient.nazwa.replace(/\s+/g, '-')}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t('toast.saved'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const scopeOptions = [
    ['all', t('report.scopeAll'), spacery.length],
    ['selected', t('report.scopeSelected'), null],
    ['pakiet', t('report.scopePackage'), pakiety.length],
  ];

  return (
    <div className="ds-card" style={{ padding: '18px 20px', marginBottom: '16px' }}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3" style={{ marginBottom: '6px' }}>
        <div className="flex items-center gap-2">
          <IconDoc className="w-3.5 h-3.5" style={{ color: 'var(--acc2)' }} />
          <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--acc2)', textTransform: 'uppercase' }}>
            {t('report.title')}
          </p>
        </div>
        <HelpHint text={t('help.clientReport')} label={t('report.title')} />
      </div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', margin: '0 0 14px', lineHeight: 1.5 }}>
        {t('report.description')}
      </p>

      {disabled ? (
        <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>
          {t('report.noData')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Zakres spacerów */}
            <div>
              <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', marginBottom: '6px', letterSpacing: '0.05em' }}>
                {t('report.scopeSpaces')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {scopeOptions.map(([v, l, n]) => {
                  const active = scope === v;
                  return (
                    <div
                      key={v}
                      onClick={() => setScope(v)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '7px 10px', borderRadius: '6px',
                        border: `1px solid ${active ? 'var(--acc)' : 'var(--border)'}`,
                        background: active ? 'rgba(184,84,40,0.07)' : 'var(--bg3)',
                        cursor: 'pointer', fontSize: '11px',
                        color: 'var(--ink)', fontWeight: active ? 600 : 400,
                      }}
                    >
                      <div style={{
                        width: '12px', height: '12px', borderRadius: '50%',
                        border: `2px solid ${active ? 'var(--acc)' : 'var(--border)'}`,
                        background: active ? 'var(--acc)' : 'transparent', flexShrink: 0,
                      }} />
                      <span style={{ flex: 1 }}>{l}</span>
                      {n !== null && <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>({n})</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Symulacja */}
            <div>
              <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', marginBottom: '6px', letterSpacing: '0.05em' }}>
                {t('report.simulationToDate')}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: symulacja ? '8px' : 0 }}>
                <div onClick={() => setSymulacja(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div style={{ width: '30px', height: '17px', borderRadius: '9px', background: symulacja ? 'var(--acc)' : 'var(--bg4)', border: '1px solid var(--border)', position: 'relative', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: '2px', left: symulacja ? '13px' : '2px', width: '11px', height: '11px', borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                  </div>
                  <span style={{ fontSize: '11px', color: 'var(--ink2)' }}>
                    {symulacja ? t('report.enabled') : t('report.disabled')}
                  </span>
                </div>
                <HelpHint text={t('help.simulationToDate')} label={t('report.simulationToDate')} />
              </div>
              {symulacja && (
                <input
                  type="date"
                  value={symData}
                  onChange={e => setSymData(e.target.value)}
                  className="ds-input"
                  style={{ fontSize: '11px' }}
                />
              )}
            </div>
          </div>

          {/* Filtr statusów */}
          <div style={{ marginTop: '12px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', marginBottom: '6px', letterSpacing: '0.05em' }}>
              {t('report.statusFilter')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {getStatusySpacer().map(st => {
                const active = statusFilter.has(st.value);
                return (
                  <button
                    key={st.value}
                    type="button"
                    onClick={() => toggleStatus(st.value)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '5px 10px', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '11px', fontWeight: active ? 600 : 400,
                      border: `1px solid ${active ? 'var(--acc)' : 'var(--border)'}`,
                      background: active ? 'rgba(184,84,40,0.07)' : 'var(--bg3)',
                      color: active ? 'var(--ink)' : 'var(--ink3)',
                    }}
                  >
                    <span style={{
                      width: '11px', height: '11px', borderRadius: '3px', flexShrink: 0,
                      border: `1px solid ${active ? 'var(--acc)' : 'var(--border)'}`,
                      background: active ? 'var(--acc)' : 'transparent',
                    }} />
                    {st.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lista spacerów do zaznaczenia */}
          {scope === 'selected' && (
            <div style={{ marginTop: '12px' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: '4px' }}>
                <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.05em', margin: 0 }}>
                  {t('report.spacesUpper')} · {t('report.selectedXofY', { selected: selectedIds.size, total: spacery.length })}
                </p>
                <button
                  onClick={() => setSelectedIds(new Set(spacery.map(s => s.id)))}
                  style={{ fontSize: '10px', color: 'var(--acc)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {t('report.selectAll')}
                </button>
              </div>
              <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg3)' }}>
                {spacery.length === 0 && (
                  <p style={{ fontSize: '11px', color: 'var(--ink3)', padding: '10px 12px' }}>{t('report.noSpaces')}</p>
                )}
                {spacery.map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '11px', color: 'var(--ink)' }}>
                    <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleId(s.id)} style={{ accentColor: 'var(--acc)' }} />
                    <span style={{ flex: 1 }}>{s.nazwa}</span>
                    <span style={{ fontSize: '10px', color: s.status === 'aktywny' ? 'var(--ok2)' : 'var(--ink3)' }}>{statusSpacerMeta(s.status).label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Wybór pakietu */}
          {scope === 'pakiet' && (
            <div style={{ marginTop: '12px' }}>
              <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', marginBottom: '4px', letterSpacing: '0.05em' }}>
                {t('report.packageUpper')}
              </p>
              {pakiety.length === 0 ? (
                <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('report.noPackages')}</p>
              ) : (
                <select value={pakietId} onChange={e => setPakietId(e.target.value)} className="ds-input" style={{ fontSize: '11px' }}>
                  {pakiety.map(p => <option key={p.id} value={p.id}>{p.nazwa} — {p.data_sprzedazy}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Footer — podsumowanie + akcja */}
          <div style={{ marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {t('report.inReport')}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--ink)', fontWeight: 600, margin: '2px 0 0' }}>
                {includedSpaces.length} {pluralPL(includedSpaces.length, t('report.space1'), t('report.space2'), t('report.space5'))}
                {symulacja && symData && symTotal !== null && (
                  <>
                    {' · '}
                    <span style={{ color: 'var(--acc)' }}>
                      {t('report.estimatedAmount')}: {formatCurrency(symTotal)}
                    </span>
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={handlePobierz}
              disabled={!canGenerate}
              className="rounded-btn font-bold transition-all"
              style={{
                padding: '9px 18px',
                border: 'none',
                background: canGenerate ? 'var(--acc)' : 'var(--bg4)',
                color: canGenerate ? '#fff' : 'var(--ink3)',
                fontSize: '12px',
                fontWeight: 700,
                cursor: canGenerate ? 'pointer' : 'not-allowed',
                opacity: canGenerate ? 1 : 0.65,
              }}
            >
              {loading ? t('report.generating') : t('report.generateAndDownload')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Plural & date helpers ────────────────────────────────────────────────────

function pluralPL(n, one, few, many) {
  const abs = Math.abs(n);
  const m10 = abs % 10;
  const m100 = abs % 100;
  if (abs === 1) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

function monthsBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const a = new Date(fromIso);
  const b = new Date(toIso);
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()));
}

// ─── Sparkline mini-wykres dla KPI Łączna sprzedaż ───────────────────────────

function Sparkline({ series }) {
  if (!series || series.length < 2) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.55 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 18, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="kdSpark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="var(--ok2)" stopOpacity={0.40} />
              <stop offset="100%" stopColor="var(--ok2)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke="var(--ok2)" strokeWidth={1.5} fill="url(#kdSpark)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── KPI z opcją kliknięcia + sparkline tłem ─────────────────────────────────

function KPI({ label, value, sub, color, onClick, sparkline, hint, helpText }) {
  const clickable = !!onClick;
  const inner = (
    <>
      {sparkline && <Sparkline series={sparkline} />}
      <div className="flex items-baseline justify-between gap-2 mb-1" style={{ position: 'relative' }}>
        <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {label}
          {helpText && (
            <span onClick={(e) => e.stopPropagation()}>
              <HelpHint text={helpText} label={label} />
            </span>
          )}
        </p>
      </div>
      <p style={{ fontSize: '20px', fontWeight: 800, fontFamily: 'monospace', color: color || 'var(--ink)', position: 'relative' }}>{value}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5" style={{ position: 'relative' }}>
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{sub}</p>}
        {clickable && (
          <span style={{ color: 'var(--ink3)', flexShrink: 0, opacity: 0.6 }} title={hint}>
            <IconChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </>
  );
  if (!clickable) {
    return <div className="ds-card p-4" style={{ position: 'relative', overflow: 'hidden' }}>{inner}</div>;
  }
  return (
    <button
      onClick={onClick}
      className="ds-card p-4 transition-all"
      style={{ textAlign: 'left', cursor: 'pointer', width: '100%', position: 'relative', overflow: 'hidden' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(184,84,40,0.4)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      {inner}
    </button>
  );
}

// ─── Breakdown helpers (kopia z DashboardV2 — stylowo spójne) ────────────────

function BreakdownLine({ label, value, color, sub, indent = false }) {
  return (
    <div className="flex items-center justify-between gap-3" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ paddingLeft: indent ? '16px' : 0, minWidth: 0, flex: 1 }}>
        <p style={{ fontSize: '12px', color: 'var(--ink2)', fontWeight: indent ? 400 : 600 }} className="truncate">{label}</p>
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>{sub}</p>}
      </div>
      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: color || 'var(--ink)', fontSize: '13px' }} className="whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}

function BreakdownTotal({ label, value, color }) {
  return (
    <div className="flex items-center justify-between gap-3" style={{ padding: '12px 0 4px', borderTop: '2px solid var(--border)', marginTop: '4px' }}>
      <p style={{ fontSize: '11px', color: 'var(--ink)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <span style={{ fontFamily: 'monospace', fontWeight: 800, color: color || 'var(--ink)', fontSize: '18px' }}>
        {value}
      </span>
    </div>
  );
}

// ─── Modale KPI breakdown ────────────────────────────────────────────────────

function AktywneSpaceryBreakdown({ spacery, onSpacerClick }) {
  const { t } = useTranslation();
  const aktywne = spacery.filter(s => s.status === 'aktywny');
  if (aktywne.length === 0) return <p style={{ fontSize: '12px', color: 'var(--ink3)' }}>—</p>;
  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.spaces2Plural', { n: aktywne.length, count: aktywne.length, label: t('dashboardV2.spaceWordOnly', { count: aktywne.length }) })}
      </p>
      <div className="flex flex-col">
        {aktywne.map(s => (
          <button
            key={s.id}
            onClick={() => onSpacerClick(s.id)}
            className="text-left w-full"
            style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <div className="flex items-center justify-between gap-3">
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{s.nazwa}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ok2)', fontSize: '12px' }}>
                {formatCurrency(s.stawka_hostingu || 0)}/{t('common.month')}
              </span>
            </div>
            {s.data_wykonania && (
              <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                {t('dashboard.fromDate', { date: formatDate(s.data_wykonania) })}
              </div>
            )}
          </button>
        ))}
      </div>
      <BreakdownTotal label={'MRR'} value={formatCurrency(aktywne.reduce((s, x) => s + (x.stawka_hostingu || 0), 0))} color="var(--ok2)" />
    </div>
  );
}

function LacznaSprzedazBreakdown({ faktury, pakiety, monthly, onClientNavigate, onClose, klientId }) {
  const { t } = useTranslation();
  const opF = faktury.filter(f => f.status === 'zaplacona');
  const opP = pakiety.filter(p => p.status === 'zaplacone');
  const sumF = opF.reduce((s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)), 0);
  const sumP = opP.reduce((s, p) => s + Number(p.kwota || 0), 0);
  const total = sumF + sumP;
  const monthsCount = monthly.filter(m => m.v > 0).length;

  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.totalSalesIntro')} {monthsCount > 0 && t('dashboardV2.monthsActivityIn', { n: monthsCount, count: monthsCount })}
      </p>
      <BreakdownLine label={t('dashboard.unpaidSectionHosting')} value={formatCurrency(sumF)} color="var(--ok2)" sub={`${opF.length} ${pluralPL(opF.length, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}`} />
      <BreakdownLine label={t('dashboard.unpaidSectionSpaces')} value={formatCurrency(sumP)} color="var(--ok2)" sub={t('dashboardV2.packageCount', { n: opP.length, count: opP.length })} />
      <BreakdownTotal label={t('dashboardV2.totalLabelInvoice')} value={formatCurrency(total)} color="var(--ok2)" />

      {monthly.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
            {t('dashboardV2.trendLast12')}
          </p>
          <div style={{ height: '90px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="kdRevTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="var(--ok2)" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="var(--ok2)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '11px', boxShadow: 'var(--shadow-card)', padding: '6px 10px' }}
                  formatter={v => [formatCurrency(v)]}
                  labelFormatter={l => l}
                />
                <Area type="monotone" dataKey="v" stroke="var(--ok2)" strokeWidth={2} fill="url(#kdRevTrend)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function NaroslaBreakdown({ spacery, onSpacerClick, onIssueInvoice, klientId }) {
  const { t } = useTranslation();
  const aktywne = spacery.filter(s => s.status === 'aktywny' && (s.kwota_narosla || 0) > 0);
  const sorted = [...aktywne].sort((a, b) => (b.kwota_narosla || 0) - (a.kwota_narosla || 0));
  const total = aktywne.reduce((s, x) => s + (x.kwota_narosla || 0), 0);

  if (sorted.length === 0) {
    return (
      <div className="text-center" style={{ padding: '24px 0', color: 'var(--ink3)', fontSize: '12px' }}>
        {t('dashboardV2.noAccruedInModal')}
      </div>
    );
  }

  const dzis = TODAY;
  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.noAccruedDescription')}
      </p>
      {sorted.map(s => {
        const months = monthsBetween(s.podstawa_naliczania, dzis);
        const color = months >= 12 ? 'var(--err2)' : months >= 3 ? 'var(--acc2)' : 'var(--ink2)';
        return (
          <button
            key={s.id}
            onClick={() => onSpacerClick(s.id)}
            className="text-left w-full"
            style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <div className="flex items-center justify-between gap-3">
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{s.nazwa}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color, fontSize: '13px' }} className="whitespace-nowrap">
                {formatCurrency(s.kwota_narosla)}
              </span>
            </div>
            <div style={{ fontSize: '10px', color: months >= 12 ? 'var(--err2)' : 'var(--ink3)', marginTop: '1px', fontWeight: months >= 12 ? 600 : 400 }}>
              {t('dashboardV2.fromDatePrefix', { date: formatDate(s.podstawa_naliczania) })}{months >= 1 && ` · ${t('dashboardV2.monthsAgo', { n: months, count: months })}`}
            </div>
          </button>
        );
      })}
      <BreakdownTotal label={t('dashboardV2.totalLabel')} value={formatCurrency(total)} color="var(--acc)" />
      <div style={{ marginTop: '16px' }}>
        <Btn onClick={onIssueInvoice} className="w-full" style={{ width: '100%' }}>
          <span className="flex items-center justify-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('dashboardV2.narosłaInvoiceCta')}</span>
        </Btn>
      </div>
    </div>
  );
}

function NiezaplaconeBreakdown({ faktury, pakiety, onClose, onClientNavigate }) {
  const { t } = useTranslation();
  const dzis = TODAY;
  const fNiezap = faktury.filter(f => f.status !== 'zaplacona');
  const pNiezap = pakiety.filter(p => p.status !== 'zaplacone');
  const sumF = fNiezap.reduce((s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)), 0);
  const sumP = pNiezap.reduce((s, p) => s + Number(p.kwota || 0), 0);
  const total = sumF + sumP;

  if (total === 0) {
    return (
      <div className="text-center" style={{ padding: '24px 0', color: 'var(--ink3)', fontSize: '12px' }}>
        {t('dashboardV2.noUnpaidItems')}
      </div>
    );
  }

  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.unpaidInvoicesPaymentDesc')}
      </p>
      {fNiezap.length > 0 && (
        <>
          <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            {t('dashboardV2.unpaidHostingSectionTitle', { n: fNiezap.length })}
          </p>
          {fNiezap.map(f => {
            const overdue = f.termin_platnosci && f.termin_platnosci < dzis;
            const suma = Number(f.suma) || (f.pozycje || []).reduce((s, p) => s + (Number(p.kwota) || 0), 0);
            return (
              <div key={`f${f.id}`} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between gap-3">
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">
                    {f.nr_faktury || formatDate(f.data_wystawienia)}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: overdue ? 'var(--err2)' : 'var(--acc2)', fontSize: '12px' }}>
                    {formatCurrency(suma)}
                  </span>
                </div>
                <div style={{ fontSize: '10px', color: overdue ? 'var(--err2)' : 'var(--ink3)', marginTop: '1px' }}>
                  {f.termin_platnosci ? t('dashboardV2.termOnDate', { date: formatDate(f.termin_platnosci) }) : '—'}{overdue && ` · ${t('dashboardV2.overdueAdjective')}`}
                </div>
              </div>
            );
          })}
        </>
      )}
      {pNiezap.length > 0 && (
        <>
          <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: fNiezap.length ? '14px' : 0, marginBottom: '6px' }}>
            {t('dashboardV2.unpaidPackagesSectionTitle', { n: pNiezap.length })}
          </p>
          {pNiezap.map(p => (
            <div key={`p${p.id}`} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between gap-3">
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{p.nazwa}</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--acc2)', fontSize: '12px' }}>
                  {formatCurrency(p.kwota)}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                {t('dashboardV2.saleOnDate', { date: formatDate(p.data_sprzedazy) })}
              </div>
            </div>
          ))}
        </>
      )}
      <BreakdownTotal label={t('dashboardV2.totalLabel')} value={formatCurrency(total)} color="var(--err2)" />
    </div>
  );
}

function KPIDetailModal({ type, data, faktury, pakiety, monthly, klientId, onClose, onNavigate, onIssueInvoice }) {
  const { t } = useTranslation();
  if (!type) return null;
  const TITLES = {
    aktywne: t('dashboardV2.modalActiveSpaces'),
    mrr: t('dashboardV2.modalMRR'),
    laczna: t('dashboardV2.modalTotalSales'),
    narosle: t('dashboardV2.modalAccrued'),
    niezaplacone: t('dashboardV2.modalUnpaid'),
  };
  const handleSpacer = (id) => { onClose(); onNavigate('spacer', id); };

  return (
    <Modal title={TITLES[type] || ''} onClose={onClose}>
      {(type === 'aktywne' || type === 'mrr') && (
        <AktywneSpaceryBreakdown spacery={data.spacery || []} onSpacerClick={handleSpacer} />
      )}
      {type === 'laczna' && (
        <LacznaSprzedazBreakdown faktury={faktury} pakiety={pakiety} monthly={monthly} klientId={klientId} onClose={onClose} onClientNavigate={onNavigate} />
      )}
      {type === 'narosle' && (
        <NaroslaBreakdown spacery={data.spacery || []} onSpacerClick={handleSpacer} klientId={klientId}
          onIssueInvoice={() => { onClose(); onIssueInvoice(); }} />
      )}
      {type === 'niezaplacone' && (
        <NiezaplaconeBreakdown faktury={faktury} pakiety={pakiety} onClose={onClose} onClientNavigate={onNavigate} />
      )}
    </Modal>
  );
}

// ─── Karta dane klienta — kompakt + auto-hide pustych sekcji + CTA ───────────

function DaneKlientaCard({ data, onEdit }) {
  const { t } = useTranslation();
  const hasContact = data.imie_nazwisko || data.telefon || data.email || data.notatki;
  const hasInvoice = data.nazwa_firmy || data.adres || data.nip;

  return (
    <div className="ds-card p-4 flex flex-col gap-4">
      {hasContact ? (
        <div>
          <h2 className="section-label">{t('clients.contactPerson')}</h2>
          <div className="space-y-1.5">
            {data.imie_nazwisko && <Row label={t('clients.fullName')} value={data.imie_nazwisko} />}
            {data.telefon && <Row label={t('clients.phone')} value={data.telefon} />}
            {data.email && <Row label={t('clients.email')} value={data.email} />}
            {data.notatki && <Row label={t('clients.notes')} value={data.notatki} multiline />}
          </div>
        </div>
      ) : (
        <div>
          <h2 className="section-label">{t('clients.contactPerson')}</h2>
          <button onClick={onEdit}
            className="text-left w-full"
            style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px dashed var(--border)', borderRadius: '8px', cursor: 'pointer' }}>
            <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('dashboardV2.settingsAddContactCta')}</span>
          </button>
        </div>
      )}

      {hasInvoice ? (
        <div>
          <h2 className="section-label">{t('clients.invoiceData')}</h2>
          <div className="space-y-1.5">
            {data.nazwa_firmy && <Row label={t('clients.companyName')} value={data.nazwa_firmy} />}
            {data.adres && <Row label={t('clients.address')} value={data.adres} />}
            {data.nip && <Row label={t('clients.nip')} value={data.nip} mono />}
          </div>
        </div>
      ) : (
        <div>
          <h2 className="section-label">{t('clients.invoiceData')}</h2>
          <button onClick={onEdit}
            className="text-left w-full"
            style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px dashed var(--border)', borderRadius: '8px', cursor: 'pointer' }}>
            <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('dashboardV2.settingsAddInvoiceDataCta')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, multiline, mono }) {
  return (
    <div className="flex gap-2">
      <span style={{ fontSize: '10px', color: 'var(--ink3)' }} className="w-20 shrink-0">{label}</span>
      <span style={{ fontSize: '11px', color: 'var(--ink)', fontFamily: mono ? 'monospace' : undefined }} className={multiline ? 'whitespace-pre-wrap' : ''}>{value}</span>
    </div>
  );
}

// ─── Karta status hostingu (kompakt, bez listy spacerów) ─────────────────────

function StatusHostinguCard({ data, faktury, onClickNiezaplacone }) {
  const { t } = useTranslation();
  const aktywne = (data.spacery || []).filter(s => s.status === 'aktywny');
  const naroslaSpacery = aktywne.filter(s => (s.kwota_narosla || 0) > 0);
  const naroslaLacznie = naroslaSpacery.reduce((s, x) => s + (x.kwota_narosla || 0), 0);
  const najstarsza = naroslaSpacery.reduce((min, s) => {
    if (!s.podstawa_naliczania) return min;
    return !min || s.podstawa_naliczania < min ? s.podstawa_naliczania : min;
  }, null);
  const najstarszaMies = najstarsza ? monthsBetween(najstarsza, TODAY) : 0;

  const fakturyNiezap = faktury.filter(f => f.status !== 'zaplacona');
  const sumaNiezap = fakturyNiezap.reduce((s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)), 0);

  return (
    <div className="ds-card p-4 flex flex-col gap-3">
      <h2 className="section-label">{t('dashboardV2.hostingStatusTitle')}</h2>

      <div className="flex justify-between items-center">
        <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('dashboardV2.kpiActiveSpaces')}</span>
        <span style={{ fontSize: '11px', color: 'var(--ink)', fontWeight: 700 }}>{aktywne.length}</span>
      </div>

      <div className="flex justify-between items-center">
        <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('dashboardV2.kpiAccrued')}</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: naroslaLacznie > 0 ? 'var(--acc2)' : 'var(--ok2)' }}>
          {naroslaLacznie > 0 ? formatCurrency(naroslaLacznie) : t('dashboardV2.kpiAccruedNone')}
        </span>
      </div>

      {najstarsza && (
        <div className="flex justify-between items-center">
          <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('dashboardV2.oldestDebt')}</span>
          <span style={{ fontSize: '11px', color: najstarszaMies >= 12 ? 'var(--err2)' : 'var(--ink2)', fontWeight: najstarszaMies >= 12 ? 700 : 600 }}>
            {najstarszaMies > 0 ? t('dashboardV2.monthsAgo', { n: najstarszaMies, count: najstarszaMies }) : t('dashboardV2.lessThanMonth')}
          </span>
        </div>
      )}

      <div className="flex justify-between items-center pt-2" style={{ borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('dashboardV2.modalUnpaid')}</span>
        {fakturyNiezap.length > 0 ? (
          <button
            onClick={onClickNiezaplacone}
            className="hover:underline"
            style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--err2)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>
            {formatCurrency(sumaNiezap)} ›
          </button>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--ok2)', fontWeight: 700 }}>{t('dashboardV2.kpiAccruedNone')}</span>
        )}
      </div>
    </div>
  );
}

// ─── Główny komponent ─────────────────────────────────────────────────────────

export default function KlientDetailV2({ klientId, onNavigate, initialAction, onActionConsumed }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [faktury, setFaktury] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalFaktura, setModalFaktura] = useState(null);
  const [modalPakiet, setModalPakiet] = useState(null);
  const [modalKlient, setModalKlient] = useState(false);
  const [spacerAction, setSpacerAction] = useState(null);
  const [kpiModal, setKpiModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.getKlient(klientId),
      api.getKlientFaktury(klientId),
    ])
      .then(([k, f]) => { setData(k); setFaktury(f || []); })
      .finally(() => setLoading(false));
  }, [klientId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (initialAction === 'add-spacer') {
      setSpacerAction('add');
      onActionConsumed?.();
    } else if (initialAction === 'add-faktura') {
      setModalFaktura('add');
      onActionConsumed?.();
    }
  }, [initialAction, onActionConsumed]);

  const handleEditKlient = async (form) => {
    await api.updateKlient(klientId, form);
    toast.success(t('toast.saved'));
    load();
  };

  // ── KPI calculations ──
  const stats = useMemo(() => {
    if (!data) return null;
    const spacery = data.spacery || [];
    const pakiety = data.pakiety || [];
    const aktywne = spacery.filter(s => s.status === 'aktywny');
    const mrr = aktywne.reduce((s, x) => s + (x.stawka_hostingu || 0), 0);
    const naroslaLacznie = aktywne.reduce((s, x) => s + (x.kwota_narosla || 0), 0);
    const opF = faktury.filter(f => f.status === 'zaplacona');
    const opP = pakiety.filter(p => p.status === 'zaplacone');
    const lacznaSprzedaz = opF.reduce((s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)), 0)
                          + opP.reduce((s, p) => s + Number(p.kwota || 0), 0);

    // Klient od — najstarszy spacer
    const klientOd = spacery.reduce((min, s) => {
      if (!s.data_wykonania) return min;
      return !min || s.data_wykonania < min ? s.data_wykonania : min;
    }, null);

    // Niezapłacone
    const fNiezap = faktury.filter(f => f.status !== 'zaplacona');
    const pNiezap = pakiety.filter(p => p.status !== 'zaplacone');
    const niezapTotal = fNiezap.reduce((s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)), 0)
                       + pNiezap.reduce((s, p) => s + Number(p.kwota || 0), 0);

    // Trend miesięczny ostatnie 12 miesięcy (do sparkline + breakdown)
    const MONTHS = getMonthsShort();
    const today = new Date();
    const monthly = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const key = `${yyyy}-${mm}`;
      const fSum = faktury.filter(f => f.status === 'zaplacona' && (f.data_wplaty || '').startsWith(key))
        .reduce((s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)), 0);
      const pSum = pakiety.filter(p => p.status === 'zaplacone' && ((p.data_platnosci || p.data_sprzedazy) || '').startsWith(key))
        .reduce((s, p) => s + Number(p.kwota || 0), 0);
      monthly.push({ name: `${MONTHS[d.getMonth()]} ${String(yyyy).slice(2)}`, v: fSum + pSum });
    }

    return {
      aktywne: aktywne.length,
      mrr,
      lacznaSprzedaz,
      naroslaLacznie,
      klientOd,
      niezapTotal,
      niezapCount: fNiezap.length + pNiezap.length,
      monthly,
    };
  }, [data, faktury]);

  if (loading) return (
    <div className="flex flex-col gap-4">
      <div className="ds-card" style={{ padding: '20px' }}>
        <Skeleton w="50%" h="20px" />
        <div style={{ marginTop: '10px' }}><Skeleton w="35%" h="11px" /></div>
      </div>
      <SkeletonCard lines={4} />
      <SkeletonCard lines={3} />
    </div>
  );
  if (!data) return <p style={{ color: 'var(--err2)' }}>{t('clientDetail.notFound')}</p>;

  const headlineName = data.nazwa_firmy || data.imie_nazwisko || data.nazwa;
  const aktywne = (data.spacery || []).filter(s => s.status === 'aktywny');
  const isEmpty = (data.spacery?.length || 0) === 0 && faktury.length === 0 && (data.pakiety?.length || 0) === 0;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => onNavigate('klienci')} style={{ fontSize: '11px', color: 'var(--ink2)' }} className="hover:underline">{t('clients.title')}</button>
        <span style={{ color: 'var(--ink3)' }}>/</span>
        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink)' }}>{headlineName}</span>
      </div>

      {/* Header akcji */}
      <div className="flex flex-wrap gap-2 mb-5">
        <Btn variant="ghost" onClick={() => setModalKlient(true)}>
          <span className="flex items-center gap-1.5"><IconEdit className="w-3.5 h-3.5" /> {t('clients.editClient')}</span>
        </Btn>
        <Btn onClick={() => setSpacerAction('add')}>
          <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('spaces.add')}</span>
        </Btn>
        <Btn onClick={() => setModalFaktura('add')} disabled={aktywne.length === 0}>
          <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('clientDetail.addInvoice')}</span>
        </Btn>
      </div>

      {/* Panel klienta — link do udostępnienia + podgląd */}
      <KlientPanelModule slug={user?.klient_slug} klient={data} />

      {/* Raport klienta — parametry i generowanie PDF */}
      <RaportModule klient={data} disabled={isEmpty} />

      {/* Stat zone — 4 KPI */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <KPI
            label={t('dashboardV2.kpiActiveSpaces')}
            value={String(stats.aktywne)}
            color="var(--ink)"
            sub={stats.klientOd ? t('dashboardV2.clientSinceDate', { date: formatDate(stats.klientOd) }) : null}
            onClick={stats.aktywne > 0 ? () => setKpiModal('aktywne') : undefined}
            hint={t('dashboardV2.hintShowActiveSpacesList')}
          />
          <KPI
            label={t('dashboardV2.kpiMRR')}
            value={formatCurrency(stats.mrr)}
            color="var(--ok2)"
            sub={t('dashboardV2.avgRateXTimes', { n: stats.aktywne })}
            onClick={stats.aktywne > 0 ? () => setKpiModal('mrr') : undefined}
            hint={t('dashboardV2.hintBreakDownIntoSpaces')}
            helpText={t('help.mrr')}
          />
          <KPI
            label={t('dashboardV2.kpiTotalSales')}
            value={formatCurrency(stats.lacznaSprzedaz)}
            color="var(--ok2)"
            sub={stats.lacznaSprzedaz > 0 ? t('dashboardV2.totalSubAllPeriod') : null}
            onClick={stats.lacznaSprzedaz > 0 ? () => setKpiModal('laczna') : undefined}
            sparkline={stats.monthly}
            hint={t('dashboardV2.hintShowMonthlyTrend')}
          />
          <KPI
            label={t('dashboardV2.kpiAccrued')}
            value={formatCurrency(stats.naroslaLacznie)}
            color={stats.naroslaLacznie > 0 ? 'var(--acc2)' : 'var(--ok2)'}
            sub={stats.niezapCount > 0 ? `+ ${formatCurrency(stats.niezapTotal)} ${t('dashboardV2.unpaidShort')}` : null}
            onClick={stats.naroslaLacznie > 0 ? () => setKpiModal('narosle') : undefined}
            hint={t('dashboardV2.hintShowSpacesWithDebt')}
            helpText={t('help.accruedDebts')}
          />
        </div>
      )}

      {/* KPI breakdown modal */}
      {stats && (
        <KPIDetailModal
          type={kpiModal}
          data={data}
          faktury={faktury}
          pakiety={data.pakiety || []}
          monthly={stats.monthly}
          klientId={klientId}
          onClose={() => setKpiModal(null)}
          onNavigate={onNavigate}
          onIssueInvoice={() => setModalFaktura('add')}
        />
      )}

      {/* Karty: dane klienta + status hostingu */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <DaneKlientaCard data={data} onEdit={() => setModalKlient(true)} />
        <StatusHostinguCard
          data={data}
          faktury={faktury}
          onClickNiezaplacone={() => setKpiModal('niezaplacone')}
        />
      </div>

      {/* Niezapłacone (auto-hide) */}
      <FakturyList
        mode="unpaid"
        faktury={faktury}
        pakiety={data.pakiety || []}
        onEditFaktura={f => setModalFaktura({ edit: f })}
        onEditPakiet={p => setModalPakiet({ edit: p })}
        onVisibilityChanged={load}
      />

      {/* Historia faktur — pokazuj tylko gdy klient ma jakieś dane */}
      {!isEmpty && (
        <FakturyList
          mode="all"
          faktury={faktury}
          pakiety={data.pakiety || []}
          onAddFaktura={() => setModalFaktura('add')}
          onEditFaktura={f => setModalFaktura({ edit: f })}
          onAddPakiet={() => setModalPakiet('add')}
          onEditPakiet={p => setModalPakiet({ edit: p })}
          onVisibilityChanged={load}
        />
      )}

      {/* Lista spacerów (Spacery z compact mode — auto-hide pustych statusów + search ≤5) */}
      <Spacery
        onNavigate={onNavigate}
        defaultKlientId={klientId}
        key={klientId}
        initialAction={spacerAction}
        onActionConsumed={() => setSpacerAction(null)}
        compact
      />

      {/* ── Modale (reużyte z KlientDetail.jsx) ───────────────────────── */}
      {modalFaktura === 'add' && (
        <WystawFaktureModal klient={data} spacery={data.spacery || []}
          onClose={() => setModalFaktura(null)} onDone={load} />
      )}
      {modalFaktura?.edit && (
        <WystawFaktureModal klient={data} spacery={data.spacery || []} faktura={modalFaktura.edit}
          onClose={() => setModalFaktura(null)} onDone={load}
          onDelete={() => setModalFaktura({ delete: modalFaktura.edit })}
          onMarkPaid={() => setModalFaktura({ mark: modalFaktura.edit })} />
      )}
      {modalFaktura?.mark && (
        <OznaczFaktureZaplaconaModal klientId={klientId} faktura={modalFaktura.mark}
          onClose={() => setModalFaktura(null)} onDone={load} />
      )}
      {modalFaktura?.delete && (
        <Modal title={t('clientDetail.deleteInvoice')} onClose={() => setModalFaktura(null)}>
          <div className="flex flex-col gap-4">
            <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>
              {t('clientDetail.deleteInvoiceConfirm')}
            </p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setModalFaktura(null)}>{t('common.cancel')}</Btn>
              <Btn variant="danger" onClick={async () => {
                await api.deleteKlientFaktura(data.id, modalFaktura.delete.id);
                setModalFaktura(null);
                load();
              }}>{t('clientDetail.deleteInvoice')}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {modalKlient && (
        <Modal title={t('clients.editClient')} onClose={() => setModalKlient(false)} wide>
          <KlientForm initial={data} onSave={handleEditKlient} onClose={() => setModalKlient(false)} />
        </Modal>
      )}

      {modalPakiet === 'add' && (
        <PakietForm klientId={klientId} pakiet={null}
          onClose={() => setModalPakiet(null)} onSaved={() => { setModalPakiet(null); load(); }} />
      )}
      {modalPakiet?.edit && (
        <PakietForm klientId={klientId} pakiet={modalPakiet.edit}
          onClose={() => setModalPakiet(null)} onSaved={() => { setModalPakiet(null); load(); }}
          onDelete={() => setModalPakiet({ delete: modalPakiet.edit })}
          onMarkPaid={() => setModalPakiet({ mark: modalPakiet.edit })} />
      )}
      {modalPakiet?.mark && (
        <ZaplacPakietModal pakiet={modalPakiet.mark}
          onClose={() => setModalPakiet(null)} onSaved={() => { setModalPakiet(null); load(); }} />
      )}
      {modalPakiet?.delete && (
        <Modal title={t('clientDetail.deletePackage')} onClose={() => setModalPakiet(null)}>
          <div className="flex flex-col gap-4">
            <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>
              {t('clientDetail.deletePackageConfirm', { name: modalPakiet.delete.nazwa })}
            </p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setModalPakiet(null)}>{t('common.cancel')}</Btn>
              <Btn variant="danger" onClick={async () => {
                await api.deletePakiet(modalPakiet.delete.id);
                setModalPakiet(null);
                load();
              }}>{t('clientDetail.deletePackage')}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
